import React, { useEffect, useState } from "react";
import { Popover } from "react-tiny-popover";
import Edit from "./img/edit.svg";
import Delete from "./img/delete.svg";
import View from "./img/view.svg";
import Trigger from "./img/trigger.svg";
import Retire from "./img/retire.svg";
import { useAuth } from "../../config/utilities/useAuth";

export default function TablePopover({
  isOpen,
  onClose,
  children,
  rowId,
  handleAction,
  module,
  canView,
  canEdit,
  row,
}) {
  const { privilege, tripTypes } = useAuth();
  const role = privilege?.role;
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // react-tiny-popover's "left" placement can render off-screen on narrow screens.
  const popoverPositions = isMobileViewport ? ["bottom"] : ["left"];

  const stopAndClose = (e) => {
    e?.stopPropagation?.();
    onClose?.();
  };

  const ViewAction = (id) => (
    <button
      className="table-popover-action"
      onClick={(e) => {
        stopAndClose(e);
        handleAction("view", id);
      }}
      key="view"
    >
      <img src={View} alt="View" />
      View
    </button>
  );

  const EditAction = (id) => (
    <button
      className="table-popover-action"
      onClick={(e) => {
        stopAndClose(e);
        handleAction("edit", id);
      }}
      key="edit"
    >
      <img src={Edit} alt="Edit" />
      Edit
    </button>
  );

  const DeleteAction = (id) => (
    <button
      className="table-popover-action delete-action"
      onClick={(e) => {
        stopAndClose(e);
        handleAction("delete", id);
      }}
      key="delete"
    >
      <img src={Delete} alt="Delete" />
      Delete
    </button>
  );

  const RetireAction = (id) => (
    <button
      className="table-popover-action delete-action"
      onClick={(e) => {
        stopAndClose(e);
        handleAction("delete", id);
      }}
      key="delete"
    >
      <img src={Retire} alt="Delete" />
      Retire
    </button>
  );

  const TriggerAction = (label, id, key) => (
    <button
      className="table-popover-action"
      onClick={(e) => {
        stopAndClose(e);
        handleAction(key, id);
      }}
      key={key}
    >
      <img src={Trigger} alt={label} />
      {label}
    </button>
  );

  const getPopoverContent = () => {
    let actions = [];
    const hasMatchingInstitutionTripType =
      module !== "trips" ||
      tripTypes?.some((tripType) => tripType?.id === row?.type);

    if (module === "stopsArray") {
      const actions = [];

      if (role !== "VIEWER") {
        // actions.push(EditAction(rowId)); - Disabled for now
        actions.push(DeleteAction(rowId));
      }

      return <div className="table-popover-content">{actions}</div>;
    }

    if (module === "associatedStudent") {
      return (
        <div className="table-popover-content">{[DeleteAction(rowId)]}</div>
      );
    }

    if (
      module === "serviceAnnouncement" ||
      module === "busPolicy" ||
      module === "generalPolicy"
    ) {
      if (role === "VIEWER") {
        actions.push(ViewAction(rowId));
      } else {
        if (!row.isActive) {
          actions.push(TriggerAction("Activate", rowId, "activate"));
        }
        actions.push(EditAction(rowId), DeleteAction(rowId));
      }

      return <div className="table-popover-content">{actions}</div>;
    }

    // notifications
    if (module === "notifications") {
      const { status, alertTime } = row;
      const now = new Date();
      const scheduledDate = new Date(alertTime?.scheduled?.datetime);

      if (status === "Published") {
        actions.push(ViewAction(rowId));
        if (role !== "VIEWER") actions.push(DeleteAction(rowId));
        return <div className="table-popover-content">{actions}</div>;
      }

      const isScheduled = alertTime?.scheduled?.isEnabled;
      const isOnDemand = alertTime?.onDemand?.isEnabled;

      if (isScheduled) {
        if (scheduledDate < now) {
          actions.push(ViewAction(rowId));
          if (role !== "VIEWER") actions.push(DeleteAction(rowId));
        } else {
          if (role !== "VIEWER") {
            actions.push(EditAction(rowId), DeleteAction(rowId));
          } else {
            actions.push(ViewAction(rowId));
          }
        }
        return <div className="table-popover-content">{actions}</div>;
      }

      if (isOnDemand) {
        actions.push(TriggerAction("Run Campaign", rowId, "runCampaign"));
        if (role !== "VIEWER") {
          actions.push(EditAction(rowId), DeleteAction(rowId));
        } else {
          actions.push(ViewAction(rowId));
        }
        return <div className="table-popover-content">{actions}</div>;
      }

      return <div className="table-popover-content">{actions}</div>;
    }

    if (module === "parent") {
      if (role === "VIEWER") {
        actions.push(ViewAction(rowId));
      } else {
        if (row.status === "inactive") {
          actions.push(TriggerAction("Activate", rowId, "activate"));
        } else {
          actions.push(TriggerAction("Deactivate", rowId, "deactivate"));
        }
        actions.push(EditAction(rowId), DeleteAction(rowId));
      }
    }

    // Viewer only (vehicles, trips, stops)
    if (
      ["vehicles", "trips", "stops", "drivers"].includes(module) &&
      role === "VIEWER"
    ) {
      return <div className="table-popover-content">{[ViewAction(rowId)]}</div>;
    }

    if (module === "admin") {
      return (
        <div className="table-popover-content">
          {canView && ViewAction(rowId)}
          {canEdit && [EditAction(rowId), DeleteAction(rowId)]}
        </div>
      );
    }

    // stops, trips, vehicles for editor/admin
    if (
      ["stops", "trips", "vehicles", "drivers"].includes(module) &&
      role !== "VIEWER"
    ) {
      if (row.status === "active" && (module !== "trips" || hasMatchingInstitutionTripType))
        actions.push(TriggerAction("Deactivate", rowId, "deactivate"));

      if (row.status === "inactive")
        actions.push(TriggerAction("Activate", rowId, "activate"));

      if (module !== "trips" || hasMatchingInstitutionTripType) {
        actions.push(EditAction(rowId));
      }

      if (module === "vehicles") {
        actions.push(RetireAction(rowId));
      } else if (module !== "drivers") {
        actions.push(DeleteAction(rowId));
      }
    }

    if (module === "scheduling") {
      const { status, scheduleDate } = row;
      const today = new Date().toISOString().split("T")[0];
      const isPastDate = scheduleDate < today;

      if (role === "VIEWER") {
        actions.push(ViewAction(rowId));
      } else {
        if (status !== "scheduled" || isPastDate) {
          actions.push(ViewAction(rowId), DeleteAction(rowId));
        } else {
          actions.push(EditAction(rowId), DeleteAction(rowId));
        }
      }

      return <div className="table-popover-content">{actions}</div>;
    }

    // family, parent, child, notifications, stops
    if (
      [
        "family",
        "child",
        "notifications",
        "scheduling",
        "notifications",
      ].includes(module)
    ) {
      if (role === "VIEWER") {
        actions.push(ViewAction(rowId));
      } else {
        actions.push(EditAction(rowId), DeleteAction(rowId));
      }
    }

    // Default fallback
    if (actions.length === 0) {
      actions.push(
        ViewAction(rowId),
        EditAction(rowId),
        module === "vehicles" ? RetireAction(rowId) : DeleteAction(rowId),
      );
    }

    // If both edit & delete exist, remove view
    const hasEdit = actions.some((a) => a.key === "edit");
    const hasDelete = actions.some((a) => a.key === "delete");
    if (hasEdit && hasDelete) {
      actions = actions.filter((a) => a.key !== "view");
    }

    return <div className="table-popover-content">{actions}</div>;
  };

  return (
    <Popover
      containerClassName="popover-container"
      isOpen={isOpen}
      positions={popoverPositions}
      align={isMobileViewport ? "start" : undefined}
      padding={isMobileViewport ? 12 : 0}
      boundaryInset={isMobileViewport ? 12 : undefined}
      onClickOutside={onClose}
      content={getPopoverContent()}
    >
      {children}
    </Popover>
  );
}
